import React, { useMemo } from "react";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

const nodeStyle = {
  padding: 8,
  borderRadius: 6,
  border: "2px solid #b8e6e6",
  background: "#ffffff",
  fontSize: 10,
  width: 160,
  height: 'auto',
  textAlign: "center",
  color: "#2d5555",
};

const highlightStyle = {
  ...nodeStyle,
  border: "3px solid #008080",
  background: "#e0f2f1",
  fontWeight: "bold",
  boxShadow: "0 4px 8px rgba(0, 128, 128, 0.3)",
};

const DecisionFlow = ({
  siteCode,
  projectedUsage,
  capacityLimit,
  canExpand,
  radioValue,
  uniquePoolNames,
  usedSpace,
  totalSpace,
  storage,
  modelInfo,
}) => {
  // Safe number formatting with NaN handling
  const formatNumber = (num) => {
    const parsed = parseFloat(num);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
  };

  // Safe values
  const safeStorage = Number.isFinite(parseFloat(storage)) ? parseFloat(storage) : 0;
  const safeUsedSpace = Number.isFinite(parseFloat(usedSpace)) ? parseFloat(usedSpace) : 0;
  const safeTotalSpace = Number.isFinite(parseFloat(totalSpace)) ? parseFloat(totalSpace) : 0;
  const safeProjectedUsage = Number.isFinite(parseFloat(projectedUsage)) ? parseFloat(projectedUsage) : 0;
  const safeCapacityLimit = Number.isFinite(parseFloat(capacityLimit)) ? parseFloat(capacityLimit) : 0;

  // Calculate gap and server requirements
  const gap = Math.max(0, safeProjectedUsage - safeCapacityLimit);
  
  // Get server type logic
  const getServerType = (siteCode, isExpansion = false) => {
    if (isExpansion && modelInfo?.tshirtSizes?.[0]) {
      return modelInfo.tshirtSizes[0];
    }
    if (siteCode === "KTC") return "DL-380";
    if (["SGP", "CTC", "AME", "NLAME"].includes(siteCode)) return "ALLETRA";
    
    // For new ROBO clusters, check if DL-320 nodes > 6, then use DL-380
    const dl320NodesNeeded = Math.ceil(gap / 41); // DL-320 capacity
    return dl320NodesNeeded > 6 ? "DL-380" : "DL-320";
  };

  // Calculate nodes needed
  const getNodesNeeded = (serverType, isExpansion = false) => {
    const capacities = { "DL-380": 133, "DL-320": 41, "ALLETRA": 333 };
    const capacity = capacities[serverType] || 41;
    
    if (isExpansion) {
      return Math.ceil(gap / capacity);
    } else {
      // New cluster: minimum 3 nodes, multiples of 3
      const nodesNeeded = Math.ceil(gap / capacity);
      return Math.max(3, Math.ceil(nodesNeeded / 3) * 3);
    }
  };

  const expansionServerType = getServerType(siteCode, true);
  const expansionNodesNeeded = getNodesNeeded(expansionServerType, true);
  const newClusterServerType = getServerType(siteCode, false);
  const newClusterNodesNeeded = getNodesNeeded(newClusterServerType, false);

  // Highlight logic - updated to match actual user selection (radioValue)
  const highlightNodes = useMemo(() => {
    if (!siteCode) return ["1", "2"];
    if (safeProjectedUsage <= safeCapacityLimit) return ["1", "3", "4", "5"];
    
    // Use radioValue to determine the actual path taken
    if (radioValue === 3) {
      // Expansion was selected
      return ["1", "3", "4", "6", "7"];
    } else if (radioValue === 2) {
      // New cluster was selected
      return ["1", "3", "4", "6", "8"];
    } else {
      // Default logic if no radioValue (before estimation)
      if (canExpand && expansionNodesNeeded <= 9) return ["1", "3", "4", "6", "7"];
      return ["1", "3", "4", "6", "8"];
    }
  }, [siteCode, safeProjectedUsage, safeCapacityLimit, canExpand, expansionNodesNeeded, radioValue]);

  const highlightEdges = useMemo(() => {
    if (!siteCode) return ["e1-2"];
    if (safeProjectedUsage <= safeCapacityLimit) return ["e1-3", "e3-4", "e4-5"];
    
    // Use radioValue to determine the actual path taken
    if (radioValue === 3) {
      // Expansion was selected
      return ["e1-3", "e3-4", "e4-6", "e6-7"];
    } else if (radioValue === 2) {
      // New cluster was selected
      return ["e1-3", "e3-4", "e4-6", "e6-8"];
    } else {
      // Default logic if no radioValue (before estimation)
      if (canExpand && expansionNodesNeeded <= 9) return ["e1-3", "e3-4", "e4-6", "e6-7"];
      return ["e1-3", "e3-4", "e4-6", "e6-8"];
    }
  }, [siteCode, safeProjectedUsage, safeCapacityLimit, canExpand, expansionNodesNeeded, radioValue]);

  const initialNodes = [
    {
      id: "1",
      type: "input",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", fontSize: 9, marginBottom: 2 }}>Start: Site Selected?</div>
            <div style={{ fontSize: 8, color: "#008080" }}>
              Site: {siteCode || "None"}
            </div>
          </div>
        )
      },
      position: { x: 200, y: 10 },
      style: highlightNodes.includes("1") ? highlightStyle : nodeStyle,
    },
    {
      id: "2",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", color: "#d32f2f", fontSize: 9 }}>No Site Selected</div>
            <div style={{ fontSize: 7, marginTop: 2 }}>Please select a valid site</div>
          </div>
        )
      },
      position: { x: 20, y: 110 },
      style: highlightNodes.includes("2") ? { ...highlightStyle, border: "3px solid #d32f2f", background: "#ffebee" } : { ...nodeStyle, border: "2px solid #ffcdd2" },
    },
    {
      id: "3",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", fontSize: 9, marginBottom: 2 }}>Calculate Projected Usage</div>
            <div style={{ fontSize: 7, color: "#008080" }}>
              Current: {formatNumber(safeUsedSpace)} TB<br/>
              Additional: {formatNumber(safeStorage)} TB<br/>
              Total: {formatNumber(safeUsedSpace + safeStorage)} TB
            </div>
          </div>
        )
      },
      position: { x: 380, y: 110 },
      style: highlightNodes.includes("3") ? highlightStyle : nodeStyle,
    },
    {
      id: "4",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", fontSize: 9, marginBottom: 2 }}>Usage Check (2029)</div>
            <div style={{ fontSize: 7, color: "#008080" }}>
              Projected: {formatNumber(safeProjectedUsage)} TB<br/>
              80% Limit: {formatNumber(safeCapacityLimit)} TB<br/>
              <span style={{ color: safeProjectedUsage <= safeCapacityLimit ? "#2e7d32" : "#d32f2f", fontSize: 7 }}>
                {safeProjectedUsage <= safeCapacityLimit ? "✓ Within limit" : "✗ Exceeds limit"}
              </span>
            </div>
          </div>
        )
      },
      position: { x: 380, y: 230 },
      style: highlightNodes.includes("4") ? highlightStyle : nodeStyle,
    },
    {
      id: "5",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", color: "#2e7d32", fontSize: 9, marginBottom: 2 }}>No New Resources</div>
            <div style={{ fontSize: 7 }}>Current capacity sufficient</div>
          </div>
        )
      },
      position: { x: 120, y: 350 },
      style: highlightNodes.includes("5") ? { ...highlightStyle, border: "3px solid #2e7d32", background: "#e8f5e8" } : { ...nodeStyle, border: "2px solid #c8e6c9" },
    },
    {
      id: "6",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", fontSize: 9, marginBottom: 2 }}>Expansion Analysis</div>
            <div style={{ fontSize: 7, color: "#008080" }}>
              Gap: {formatNumber(gap)} TB<br/>
              {canExpand ? (
                <>
                  Existing Type: {expansionServerType}<br/>
                  Nodes Needed: {expansionNodesNeeded}<br/>
                  <span style={{ color: "#2e7d32", fontSize: 7 }}>
                    Gen11: ✓ Available
                  </span><br/>
                  <span style={{ color: expansionNodesNeeded <= 9 ? "#2e7d32" : "#d32f2f", fontSize: 7 }}>
                    {expansionNodesNeeded <= 9 ? "✓ ≤9 nodes (12 max)" : "✗ >9 nodes (12 max)"}
                  </span><br/>
                  {radioValue === 2 && expansionNodesNeeded <= 9 && (
                    <span style={{ fontSize: 6, color: "#f57c00", fontStyle: "italic" }}>
                      Manual override: New cluster chosen
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span style={{ color: "#d32f2f", fontSize: 7 }}>
                    Gen11: ✗ None available
                  </span><br/>
                  <span style={{ fontSize: 6, fontStyle: "italic" }}>
                    Must create new cluster
                  </span><br/>
                </>
              )}
              {uniquePoolNames.length > 0 && (
                <span style={{ fontSize: 6 }}>
                  Pool: {uniquePoolNames[0]}
                </span>
              )}
            </div>
          </div>
        )
      },
      position: { x: 640, y: 350 },
      style: highlightNodes.includes("6") ? highlightStyle : nodeStyle,
    },
    {
      id: "7",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", color: "#1976d2", fontSize: 9, marginBottom: 2 }}>Expand Cluster</div>
            <div style={{ fontSize: 7 }}>
              Server: {expansionServerType}<br/>
              Add: {expansionNodesNeeded} nodes<br/>
              Total: {3 + expansionNodesNeeded} nodes<br/>
              <span style={{ fontSize: 6, fontStyle: "italic" }}>
                Uses existing hardware type
              </span>
            </div>
          </div>
        )
      },
      position: { x: 520, y: 470 },
      style: highlightNodes.includes("7") ? { ...highlightStyle, border: "3px solid #1976d2", background: "#e3f2fd" } : { ...nodeStyle, border: "2px solid #bbdefb" },
    },
    {
      id: "8",
      data: { 
        label: (
          <div style={{ padding: 4 }}>
            <div style={{ fontWeight: "bold", color: "#f57c00", fontSize: 9, marginBottom: 2 }}>New Cluster</div>
            <div style={{ fontSize: 7 }}>
              Server: {newClusterServerType}<br/>
              Nodes: {newClusterNodesNeeded}<br/>
              <span style={{ fontSize: 6, fontStyle: "italic" }}>
                {siteCode === "KTC" ? "KTC → DL-380" : 
                 ["SGP", "CTC", "AME", "NLAME"].includes(siteCode) ? "Data Center → ALLETRA" :
                 newClusterServerType === "DL-380" ? "ROBO → DL-380 (>6 DL-320)" : "ROBO → DL-320"}
              </span>
            </div>
          </div>
        )
      },
      position: { x: 760, y: 470 },
      style: highlightNodes.includes("8") ? { ...highlightStyle, border: "3px solid #f57c00", background: "#fff3e0" } : { ...nodeStyle, border: "2px solid #ffcc02" },
    },
  ];

  const initialEdges = [
    {
      id: "e1-2",
      source: "1",
      target: "2",
      label: "No",
      animated: highlightEdges.includes("e1-2"),
      style: { stroke: highlightEdges.includes("e1-2") ? "#d32f2f" : "#999", strokeWidth: 2 },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      labelStyle: { fill: "#d32f2f", fontWeight: "bold", fontSize: 10 },
    },
    {
      id: "e1-3",
      source: "1",
      target: "3",
      label: "Yes",
      animated: highlightEdges.includes("e1-3"),
      style: { stroke: highlightEdges.includes("e1-3") ? "#008080" : "#999", strokeWidth: 2 },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      labelStyle: { fill: "#008080", fontWeight: "bold", fontSize: 10 },
    },
    {
      id: "e3-4",
      source: "3",
      target: "4",
      animated: highlightEdges.includes("e3-4"),
      style: { stroke: highlightEdges.includes("e3-4") ? "#008080" : "#999", strokeWidth: 2 },
    },
    {
      id: "e4-5",
      source: "4",
      target: "5",
      label: "≤ 80%",
      animated: highlightEdges.includes("e4-5"),
      style: { stroke: highlightEdges.includes("e4-5") ? "#2e7d32" : "#999", strokeWidth: 2 },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      labelStyle: { fill: "#2e7d32", fontWeight: "bold", fontSize: 10 },
    },
    {
      id: "e4-6",
      source: "4",
      target: "6",
      label: "> 80%",
      animated: highlightEdges.includes("e4-6"),
      style: { stroke: highlightEdges.includes("e4-6") ? "#008080" : "#999", strokeWidth: 2 },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      labelStyle: { fill: "#008080", fontWeight: "bold", fontSize: 10 },
    },
    {
      id: "e6-7",
      source: "6",
      target: "7",
      label: "Yes & ≤12",
      animated: highlightEdges.includes("e6-7"),
      style: { stroke: highlightEdges.includes("e6-7") ? "#1976d2" : "#999", strokeWidth: 2 },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      labelStyle: { fill: "#1976d2", fontWeight: "bold", fontSize: 10 },
    },
    {
      id: "e6-8",
      source: "6",
      target: "8",
      label: "No or >12",
      animated: highlightEdges.includes("e6-8"),
      style: { stroke: highlightEdges.includes("e6-8") ? "#f57c00" : "#999", strokeWidth: 2 },
      labelBgStyle: { fill: "#fff", fillOpacity: 0.8 },
      labelStyle: { fill: "#f57c00", fontWeight: "bold", fontSize: 10 },
    },
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes and edges when highlight changes
  React.useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [highlightNodes, highlightEdges, siteCode, safeProjectedUsage, safeCapacityLimit, canExpand, uniquePoolNames, safeUsedSpace, safeTotalSpace, safeStorage, expansionNodesNeeded]);

   return (
    <div style={{ 
      height: '100%',
      width: '100%',
      border: "2px solid #008080", 
      borderRadius: 8,
      backgroundColor: "#f0fdfd",
      overflow: 'hidden'
    }}>
      <div style={{ 
        padding: "8px 12px", 
        backgroundColor: "#008080", 
        color: "white", 
        fontWeight: "bold",
        fontSize: 14,
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6
      }}>
        CommVault Decision Flow - Real-time Analysis ({new Date().getFullYear()}-{new Date().getFullYear() + 4})
      </div>
      <div style={{ height: 'calc(100% - 40px)', width: '100%' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView
          fitViewOptions={{ 
            padding: 0.15,
            includeHiddenNodes: false,
            minZoom: 0.8,
            maxZoom: 1.2
          }}
          proOptions={{ hideAttribution: true }}
          style={{ width: '100%', height: '100%' }}
        >
          <Controls 
            showZoom={true}
            showFitView={true}
            showInteractive={false}
            style={{
              button: {
                backgroundColor: "#008080",
                color: "white",
                border: "1px solid #008080",
                fontSize: 11
              }
            }}
          />
          <Background 
            color="#008080" 
            gap={16} 
            size={0.5}
            variant="dots"
          />
        </ReactFlow>
      </div>
    </div>
  );
};

export default DecisionFlow;